import React from "react";

interface Review {
    id: number;
    customer_name: string;
    customer_image?: string | null;
    rating: number;
    review_text: string;
    product_name?: string | null;
    is_verified: boolean;
    created_at: string;
}

interface ReviewCardProps {
    review: Review;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review }) => {

    const getInitials = (name: string) => {
        return name
            .trim()
            .split(/\s+/)
            .map(word => word.charAt(0))
            .slice(0, 2)
            .join("")
            .toUpperCase();
    };


    const formatDate = (date: string) => {
        return new Date(date).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric"
        });
    };


    return (
        <article className="
            group
            bg-white
            border border-gray-200
            rounded-2xl
            p-5
            shadow-[0_2px_10px_rgba(0,0,0,0.04)]
            hover:shadow-[0_8px_25px_rgba(0,0,0,0.08)]
            hover:-translate-y-0.5
            transition-all
            duration-300
            h-full
        ">

            {/* Customer Header */}
            <div className="flex items-start justify-between gap-3">

                <div className="flex items-center gap-3 min-w-0">

                    {review.customer_image ? (

                        <img
                            src={review.customer_image}
                            alt={review.customer_name}
                            className="
                                w-11 h-11
                                rounded-full
                                object-cover
                                shrink-0
                                ring-2 ring-green-50
                            "
                        />

                    ) : (

                        <div className="
                            w-11 h-11
                            rounded-full
                            shrink-0
                            bg-green-700
                            text-white
                            flex
                            items-center
                            justify-center
                            font-semibold
                            text-sm
                        ">
                            {getInitials(review.customer_name)}
                        </div>

                    )}


                    <div className="min-w-0">

                        <h3 className="
                            font-semibold
                            text-gray-900
                            truncate
                        ">
                            {review.customer_name}
                        </h3>


                        {review.is_verified && (

                            <div className="
                                flex
                                items-center
                                gap-1
                                text-xs
                                text-green-700
                                mt-0.5
                            ">

                                <span className="font-medium">
                                    ✓ Verified Purchase
                                </span>

                            </div>

                        )}

                    </div>

                </div>


                <span className="
                    text-xs
                    text-gray-400
                    whitespace-nowrap
                    pt-1
                ">
                    {formatDate(review.created_at)}
                </span>

            </div>


            {/* Rating */}
            <div className="flex items-center gap-0.5 mt-4">

                {[1, 2, 3, 4, 5].map(star => (

                    <span
                        key={star}
                        className={`
                            text-[18px]
                            leading-none
                            ${
                                star <= review.rating
                                    ? "text-[#fbbc04]"
                                    : "text-gray-200"
                            }
                        `}
                    >
                        ★
                    </span>

                ))}

            </div>


            {/* Review Text */}
            <p className="
                text-[15px]
                text-gray-700
                leading-6
                mt-3
            ">
                "{review.review_text}"
            </p>


            {/* Product */}
            {review.product_name && (

                <div className="
                    mt-4
                    pt-4
                    border-t border-gray-100
                ">

                    <div className="
                        inline-flex
                        items-center
                        gap-2
                        bg-green-50
                        border border-green-100
                        rounded-lg
                        px-3
                        py-2
                    ">

                        <span className="text-xs text-gray-500">
                            Product
                        </span>

                        <span className="
                            text-sm
                            font-semibold
                            text-green-800
                        ">
                            {review.product_name}
                        </span>

                    </div>

                </div>

            )}

        </article>
    );
};

export default ReviewCard;